import { Component, ErrorInfo, ReactNode } from 'react'
import { t } from '@lingui/core/macro'
import { Typography, Button } from '@mui/material'
import { colors } from '@global/constants/colors'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class LiveblocksErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Liveblocks error:', error, errorInfo)
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null })
    window.location.reload()
  }

  public render() {
    const { hasError, error } = this.state
    const { fallback, children } = this.props

    if (hasError) {
      if (fallback) {
        return fallback
      }

      return (
        <div
          style={{
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            backgroundColor: colors.gray.light,
            borderRadius: '4px',
          }}
        >
          <Typography variant="h6" color="error">
            {t`Error connecting to comments service`}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {error?.message || t`An unexpected error occurred`}
          </Typography>
          <Button variant="contained" color="primary" onClick={this.handleRetry}>
            {t`Retry`}
          </Button>
        </div>
      )
    }

    return children
  }
}
